'use client'

import React from 'react'

import { useFormattedDate } from './date-time.hook'

import type { DateFormatName, DateFormatOverrides } from './date-time.constants'

export interface DateTimeProps {
  /**
   * The date to format (Date object, timestamp, or date string)
   */
  date: Date | number | string

  /**
   * The semantic format name for display
   * The format name determines both the display format and whether the datetime attribute includes time.
   */
  formatName: DateFormatName

  /**
   * Optional custom date-fns overrides for this specific component
   */
  overrides?: DateFormatOverrides

  /**
   * Optional CSS class name for the time element
   */
  className?: string

  /**
   * Optional inline styles for the time element
   */
  style?: React.CSSProperties

  /**
   * Optional data-testid for testing
   */
  'data-testid'?: string
}

/**
 * DateTime component renders a date/time within an HTML <time> element
 * for improved accessibility and SEO.
 *
 * The <time> element provides machine-readable datetime information via the
 * `datetime` attribute (ISO 8601 format) while displaying a human-readable
 * locale-aware formatted date to users.
 *
 */
export function DateTime({ date, formatName, overrides, className, style, 'data-testid': dataTestId }: DateTimeProps) {
  const result = useFormattedDate(date, formatName, { overrides })

  if (!result) {
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { formatted, iso } = result

  return (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    <time dateTime={iso} className={className} style={style} data-testid={dataTestId}>
      {formatted}
    </time>
  )
}

DateTime.displayName = 'DateTime'

export default DateTime
